import type { CommentAnchor, DbComment } from '@domain/schemas/comment.schema';
import { anchorTextOf } from '@root/helpers/comments/anchor-text';

export interface CommentThread {
  /** `thread_id`, or the comment's own id for legacy threadless comments. */
  id: string;
  blockId: string;
  root: DbComment;
  comments: DbComment[];
  isResolved: boolean;
  isAnchored: boolean;
  isDetached: boolean;
}

const threadKey = (comment: DbComment): string => comment.thread_id ?? comment.id;

const byCreatedAt = (a: DbComment, b: DbComment): number =>
  new Date(a.created_at).getTime() - new Date(b.created_at).getTime() || a.id.localeCompare(b.id);

export const groupThreads = (comments: DbComment[] = []): CommentThread[] => {
  const groups = new Map<string, DbComment[]>();
  comments.forEach((comment) => {
    const key = threadKey(comment);
    groups.set(key, [...(groups.get(key) ?? []), comment]);
  });

  return [...groups.entries()]
    .map(([id, group]) => {
      const sorted = [...group].sort(byCreatedAt);
      const root = sorted[0];
      const hasAnchor = Boolean(root.anchor);
      return {
        id,
        blockId: root.block_id,
        root,
        comments: sorted,
        isResolved: root.is_resolved === true,
        isAnchored: hasAnchor && !root.anchor_detached_at,
        isDetached: hasAnchor && Boolean(root.anchor_detached_at),
      };
    })
    .sort((a, b) => byCreatedAt(a.root, b.root));
};

export const getBlockThreads = (blockId: string, comments?: DbComment[]): CommentThread[] =>
  groupThreads(comments).filter((thread) => thread.blockId === blockId);

/** Replies to a legacy threadless root carry its id, which `groupThreads` resolves to the same thread. */
export const getThreadReplyId = (thread: CommentThread): string => thread.root.thread_id ?? thread.root.id;

/** A write path the backend remap missed leaves the stored slice out of step; treat it as detached (spec §7.1). */
export const isAnchorStale = (anchor: CommentAnchor, blockContent: string | undefined): boolean =>
  anchorTextOf(blockContent).slice(anchor.start, anchor.end) !== anchor.text;
